import { BillingLineDto } from './billingLine.model';
import { PaymentLink, PaymentLinkStatus } from './paymentLink.model';

/**
 * Pure, React-free derivations over a line's payment link, shared by the PDV receivables rows,
 * the chase dashboard and the drawer cards, following the `deskPayment.utils.ts` precedent: a
 * rule is stated once and cannot drift between surfaces. Anything needing hooks or i18n belongs
 * in the components / `paymentLink.labels.ts`.
 */

/** The only line field the derivations below read; both line DTOs structurally satisfy it. */
export type PaymentLinkLineTarget = Pick<BillingLineDto, 'storedStatus'>;

/**
 * The link's status as of the caller's clock. The backend already serves an outlived OPEN
 * session as EXPIRED, but a page left open on the desk outlives its own response — without this
 * the row would keep offering a link nobody can pay any more. Nothing polls a link (the webhook
 * and the next refetch are what report a payment), so the caller has to advance that clock.
 */
export const effectivePaymentLinkStatus = (
  link: PaymentLink,
  now: number
): PaymentLinkStatus => {
  if (link.status !== 'OPEN') {
    return link.status;
  }
  // A session quoting no deadline (or an unparsable one) is not quoting one the desk can hand
  // to a patient, so it reads as dead rather than as payable forever: `NaN > now` is false.
  const deadline = link.expiresAt ? Date.parse(link.expiresAt) : NaN;
  return deadline > now ? 'OPEN' : 'EXPIRED';
};

/**
 * What a line's payment-link pill has to say, wording and formatting left to the pill itself:
 * - `sent` — the link travelled to the patient and is still payable;
 * - `created` — a link exists but was only ever copied at the desk;
 * - `expired` — it outlived its deadline, so what the line owes has to be chased another way;
 * - `paid` — the patient paid on it *and* the money was posted to the line. The line is PAID by
 *   then and its own status chip says so; this pill is what explains *how*;
 * - `unbooked` — the patient paid on it and the money was NOT posted to the line: it sits on
 *   Stripe and is owed back. Nothing here may read as a settlement.
 */
export type PaymentLinkPillState =
  | { kind: 'sent'; amount: number; sentAt: string; expiresAt: string | null }
  | { kind: 'created'; createdDate: string; expiresAt: string | null }
  | { kind: 'expired'; expiresAt: string | null }
  | { kind: 'paid'; amount: number }
  | { kind: 'unbooked'; amount: number };

/**
 * The payment-link state a billing line shows next to its own status, or `undefined` when there
 * is nothing to add. The single place the wording is decided, so no two surfaces can word the
 * same link differently.
 *
 * A dead link is dropped on a settled line — it advertises nothing anybody can act on — while a
 * live one is kept whatever the line's status: money reaching a link after the line was settled
 * another way is exactly what STRIPE_LINK_PAYMENT_UNBOOKED reports, and the desk has to know
 * such a link is still out there.
 */
export const derivePaymentLinkPill = (
  line: PaymentLinkLineTarget,
  link: PaymentLink | null | undefined,
  /** The clock `expiresAt` is compared against; the caller owns when it advances. */
  now: number
): PaymentLinkPillState | undefined => {
  if (!link) {
    return undefined;
  }
  switch (effectivePaymentLinkStatus(link, now)) {
    case 'PAID':
      // Only `booked === true` is the backend saying the money reached the line; `false` is an
      // explicit STRIPE_LINK_PAYMENT_UNBOOKED and `null`/absent is a backend that has not said
      // so yet. Neither may be worded as a settlement, so both fall to `unbooked`.
      return link.booked === true
        ? { kind: 'paid', amount: link.amount }
        : { kind: 'unbooked', amount: link.amount };
    case 'EXPIRED':
      return line.storedStatus === 'OPEN'
        ? { kind: 'expired', expiresAt: link.expiresAt }
        : undefined;
    default:
      return link.sentAt
        ? {
            kind: 'sent',
            amount: link.amount,
            sentAt: link.sentAt,
            expiresAt: link.expiresAt
          }
        : {
            kind: 'created',
            createdDate: link.createdDate,
            expiresAt: link.expiresAt
          };
  }
};
